import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileSignature, CheckCircle, Clock, AlertCircle, 
  Eye, Download, Filter, Search, Building2, Calendar,
  Shield, FileText, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminLegalAgreementsPanel = () => {
  const [agreements, setAgreements] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, signed: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', type: '' });
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [documentContent, setDocumentContent] = useState('');

  useEffect(() => {
    fetchAgreements();
  }, [filter]);

  const fetchAgreements = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.type) params.append('agreement_type', filter.type);
      
      const res = await axios.get(`${API_URL}/api/legal-agreements/admin/all?${params}`);
      setAgreements(res.data.agreements || []);
      setStats(res.data.stats || { total: 0, pending: 0, signed: 0 });
    } catch (error) {
      console.error('Error fetching agreements:', error);
      toast.error('Error al cargar los acuerdos');
    } finally {
      setLoading(false);
    }
  };

  const viewAgreementDocument = async (agreement) => {
    try {
      const res = await axios.get(`${API_URL}/api/legal-agreements/${agreement.agreement_id}/document`);
      setDocumentContent(res.data.content);
      setSelectedAgreement(agreement);
    } catch (error) {
      toast.error('Error al cargar el documento');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: Clock, color: 'bg-amber-100 text-amber-700', label: 'Pendiente' },
      signed: { icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700', label: 'Firmado' },
      expired: { icon: AlertCircle, color: 'bg-red-100 text-red-700', label: 'Expirado' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const types = {
      dpa: { icon: Shield, color: 'bg-blue-100 text-blue-700', label: 'DPA' },
      partnership: { icon: FileText, color: 'bg-purple-100 text-purple-700', label: 'Partnership' }
    };
    const badge = types[type] || types.dpa;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FileSignature className="w-7 h-7 text-local-sage" />
            Acuerdos Legales
          </h2>
          <p className="text-gray-500 mt-1">
            Gestión de DPA y Partnership Agreements
          </p>
        </div>
        <Button variant="outline" onClick={fetchAgreements}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
              <FileSignature className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-sm text-gray-500">Total Acuerdos</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">{stats.signed}</p>
              <p className="text-sm text-gray-500">Firmados</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
              <p className="text-sm text-gray-500">Pendientes</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">{stats.dpa || 0}</p>
              <p className="text-sm text-gray-500">DPAs</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium">Filtros:</span>
        </div>
        
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
        >
          <option value="">Todos los estados</option>
          <option value="pending">Pendientes</option>
          <option value="signed">Firmados</option>
          <option value="expired">Expirados</option>
        </select>

        <select
          value={filter.type}
          onChange={(e) => setFilter({ ...filter, type: e.target.value })}
          className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
        >
          <option value="">Todos los tipos</option>
          <option value="dpa">DPA</option>
          <option value="partnership">Partnership</option>
        </select>
      </div>

      {/* Agreements Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-local-sage" />
          </div>
        ) : agreements.length === 0 ? (
          <div className="text-center py-12">
            <FileSignature className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay acuerdos que mostrar</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="text-left p-4 font-medium text-gray-600 dark:text-gray-300">Negocio</th>
                  <th className="text-left p-4 font-medium text-gray-600 dark:text-gray-300">Tipo</th>
                  <th className="text-left p-4 font-medium text-gray-600 dark:text-gray-300">Estado</th>
                  <th className="text-left p-4 font-medium text-gray-600 dark:text-gray-300">Creado</th>
                  <th className="text-left p-4 font-medium text-gray-600 dark:text-gray-300">Firmado</th>
                  <th className="text-left p-4 font-medium text-gray-600 dark:text-gray-300">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {agreements.map((agreement) => (
                  <tr key={agreement.agreement_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-local-sage/10 flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-local-sage" />
                        </div>
                        <div>
                          <p className="font-medium">{agreement.business_data?.name || 'N/A'}</p>
                          <p className="text-xs text-gray-500">{agreement.agreement_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">{getTypeBadge(agreement.type)}</td>
                    <td className="p-4">{getStatusBadge(agreement.status)}</td>
                    <td className="p-4 text-sm text-gray-500">
                      {new Date(agreement.created_at).toLocaleDateString('es-ES')}
                    </td>
                    <td className="p-4 text-sm">
                      {agreement.signed_at ? (
                        <div>
                          <p>{new Date(agreement.signed_at).toLocaleDateString('es-ES')}</p>
                          <p className="text-xs text-gray-500">IP: {agreement.signature_ip}</p>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="p-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => viewAgreementDocument(agreement)}
                        data-testid={`view-agreement-${agreement.agreement_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Document Modal */}
      {selectedAgreement && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedAgreement(null)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="font-bold">{selectedAgreement.agreement_id}</h3>
                <div className="flex items-center gap-2 mt-1">
                  {getTypeBadge(selectedAgreement.type)}
                  {getStatusBadge(selectedAgreement.status)}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const printWindow = window.open('', '_blank');
                    printWindow.document.write(documentContent);
                    printWindow.document.close();
                    printWindow.print();
                  }}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Imprimir
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setSelectedAgreement(null)}>
                  ✕
                </Button>
              </div>
            </div>
            <div className="p-6 overflow-auto max-h-[calc(90vh-100px)]">
              {selectedAgreement.status === 'signed' && (
                <div className="mb-4 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-200">
                  <h4 className="font-semibold text-emerald-800 dark:text-emerald-300 mb-2">
                    Detalles de Firma
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Firmante:</span>
                      <span className="ml-2 font-medium">{selectedAgreement.signer_name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Email:</span>
                      <span className="ml-2 font-medium">{selectedAgreement.signer_email}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">IP:</span>
                      <span className="ml-2 font-medium">{selectedAgreement.signature_ip}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Hash:</span>
                      <span className="ml-2 font-mono text-xs">{selectedAgreement.signature_hash?.slice(0, 16)}...</span>
                    </div>
                  </div>
                </div>
              )}
              <div 
                className="prose dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: documentContent }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
